import { HStack, useToast } from '@chakra-ui/react';
import { Bookmark, Share2, ThumbsDown, ThumbsUp } from 'lucide-react';
import useContentReactions from '@/hooks/content/useContentReactions';
import ReactionButton from '@/modules/content/feed/reactions/ReactionButton';
import ReportMenu from '@/modules/content/feed/reactions/ReportMenu';
import { useAppStore } from '@/store/useAppStore';
import { TContent } from '@/types/Content.types';

// Reader reactions, backed by the real reactions API. State comes from the item's
// server-provided `viewerState` (patched optimistically by the shared reactions hook), so
// the same like/save shows up on the card, the article page and the focused loop.
const ContentReactions = ({ content }: { content: TContent }) => {
  const toast = useToast();
  const userId = useAppStore((state) => state.user?.user_id);
  const isOwnContent = userId === content.authorUserId;
  const { liked, saved, likeCount, saveCount, toggleLike, toggleSave, share, report, isNotInterested, toggleNotInterested } =
    useContentReactions(content);

  // Not-interested hard-drops the item from every feed surface for the requester; the
  // toast confirms it was recorded. The flag is owned by the hook so a rejected request
  // rolls the button back rather than leaving it claiming a mark that was never recorded.
  const onNotInterested = () => {
    const isMarking = !isNotInterested;
    toggleNotInterested();
    if (isMarking) {
      toast({ status: 'info', title: 'Got it — you won’t see this again', duration: 2500, isClosable: true });
    }
  };

  return (
    <HStack gap="14px" justify="center" paddingY="8px">
      <ReactionButton icon={ThumbsUp} label={liked ? 'Liked' : 'Helpful'} active={liked} filled count={likeCount} onClick={toggleLike} />
      {!isOwnContent && (
        <ReactionButton
          icon={ThumbsDown}
          label={isNotInterested ? 'Marked as not interested' : 'Not interested'}
          active={isNotInterested}
          filled
          onClick={onNotInterested}
        />
      )}
      <ReactionButton icon={Bookmark} label={saved ? 'Saved' : 'Save'} active={saved} filled count={saveCount} onClick={toggleSave} />
      <ReactionButton icon={Share2} label="Share" onClick={() => share(window.location.href)} />
      {!isOwnContent && <ReportMenu onReport={(reason) => report({ reason })} />}
    </HStack>
  );
};

export default ContentReactions;
